import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useLanguage } from "../contexts/LanguageContext";
import "../styles/main.css";

export default function DashboardLayout({ session, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  
  const [showDropdown, setShowDropdown] = useState(false);
  const [userName, setUserName] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (session?.user?.email) {
      setUserName(session.user.email.split('@')[0]);
    }
  }, [session]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const closeDropdown = (e) => {
      if (showDropdown && !e.target.closest('.user-menu-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, [showDropdown]);

  const isActive = (path) => location.pathname === path;

  const sidebarLinkStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    color: active ? '#007bff' : '#555',
    backgroundColor: active ? '#e3f2fd' : 'transparent',
    fontWeight: active ? '600' : '500',
    transition: 'all 0.2s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    fontSize: '0.95rem'
  });

  const dropdownItemStyle = {
    display: 'block', width: '100%', padding: '12px 16px',
    textAlign: 'left', background: 'none', border: 'none',
    fontSize: '0.95rem', cursor: 'pointer', color: '#333',
    transition: 'background 0.2s'
  };

  return (
    <div className="desti-container dashboard-theme" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* --- TOP HEADER BAR --- */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #eef0f2',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
      }}>
        {/* Left: Logo */}
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
           <h2 style={{ margin: 0, color: '#333', fontSize: '1.4rem', fontWeight: '800', letterSpacing: '-0.5px' }}>Desti.</h2>
        </div>

        {/* Right: Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {location.pathname !== '/profile' && (
            <button
              className="lang-toggle"
              onClick={() => {
                const next = language === "en" ? "zh" : "en";
                setLanguage(next);
                sessionStorage.setItem("desti_lang", next);
              }}
              style={{ 
                padding: '4px 12px', 
                fontSize: '0.85rem', 
                background: 'transparent', 
                borderRadius: '20px',
                border: '1px solid #0079d3',
                cursor: 'pointer',
                fontWeight: '700',
                color: '#0079d3',
                width: 'auto',
                marginBottom: 0
              }}
            >
              {language === "en" ? "中文" : "En"}
            </button>
          )}

          <div className="user-menu-container" style={{ position: 'relative' }}>
            <button 
              className="avatar-btn" 
              onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}
              style={{
                width: '32px', height: '32px', borderRadius: '4px',
                fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 0,
                padding: 0
              }}
            >
              {(userName || "U").charAt(0).toUpperCase()}
            </button>

            {showDropdown && (
              <div className="dropdown-menu" style={{
                position: 'absolute', top: '40px', right: '0',
                borderRadius: '4px',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                width: '200px', zIndex: 100, overflow: 'hidden', 
                animation: 'fadeIn 0.1s ease'
              }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #edeff1', backgroundColor: '#f6f7f8' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333', marginBottom: '2px' }}>{userName}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session?.user?.email}</div>
                </div>
                <button className="dropdown-item" onClick={() => navigate('/dashboard')} style={dropdownItemStyle}>
                   {language === "en" ? "📊 Dashboard" : "📊 仪表板"}
                </button>
                <button className="dropdown-item danger" onClick={() => setShowLogoutConfirm(true)} style={{ ...dropdownItemStyle, color: '#dc3545' }}>
                   {language === "en" ? "🚪 Log Out" : "🚪 退出登录"}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* --- MAIN LAYOUT (Sidebar + Content) --- */}
      <div style={{ display: 'flex', flex: 1, width: '100%', backgroundColor: '#f8f9fa' }}>
        
        {/* --- LEFT SIDEBAR (Sticky Aside) --- */}
        <aside className="dashboard-sidebar" style={{ 
          width: '260px', 
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: '64px', // Aligns exactly below the 64px header
          height: 'calc(100vh - 64px)', 
          flexShrink: 0, 
          overflowY: 'auto',
          borderRight: '1px solid #edeff1', 
          backgroundColor: '#ffffff',
          zIndex: 10
        }}>
          <div style={{ flex: 1 }}>
            <button onClick={() => navigate('/dashboard')} style={sidebarLinkStyle(isActive('/dashboard'))}>
              <span style={{ fontSize: '1.2rem' }}>📊</span> {language === "en" ? "Dashboard" : "仪表板"}
            </button>

            <button onClick={() => navigate('/fortune')} style={sidebarLinkStyle(isActive('/fortune'))}>
              <span style={{ fontSize: '1.2rem' }}>🔮</span> {language === "en" ? "Fortune" : "运势分析"}
            </button>

            <button onClick={() => navigate('/profile')} style={sidebarLinkStyle(isActive('/profile') || isActive('/edit-profile'))}>

              <span style={{ fontSize: '1.2rem' }}>👤</span> {language === "en" ? "Profile" : "个人资料"}
            </button>
            

          </div>
        </aside>

        {/* --- MAIN CONTENT AREA (Scrollable) --- */}
        <main style={{ flex: 1, padding: '40px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          {children}
        </main>

      </div>

      {/* --- LOGOUT MODAL --- */}
      {showLogoutConfirm && (
        <div className="auth-modal-backdrop" onClick={() => setShowLogoutConfirm(false)} style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ 
            backgroundColor: 'white', padding: '30px', borderRadius: '16px', 
            textAlign: 'center', minWidth: '320px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{language === "en" ? "Log Out?" : "确认登出?"}</h3>
            <p style={{ color: '#666', marginBottom: '25px' }}>{language === "en" ? "Are you sure you want to sign out?" : "您确定要退出登录吗?"}</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ padding: '10px 24px', backgroundColor: '#f8f9fa', color: '#333', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                 {language === "en" ? "Cancel" : "取消"}
              </button>
              <button onClick={async () => { await supabase.auth.signOut(); navigate("/"); }} style={{ padding: '10px 24px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', boxShadow: '0 2px 8px rgba(220,53,69,0.3)' }}>
                 {language === "en" ? "Log Out" : "确认登出"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}